import clsx, { type ClassValue } from "clsx";
import { extendTailwindMerge, validators } from "tailwind-merge";

const { isNumber, isArbitraryLength, isArbitraryVariableLength } = validators;

/**
 * Conflict resolution has to be taught our scales, or it silently drops a class
 * it cannot classify. Every entry below came from diffing clsx-only output
 * against merged output across all 4,579 class literals in the repo; the
 * measured one was layered cards, which lost their background colour and
 * rendered transparent. Custom values come from packages/tokens/src/tokens.css
 * and packages/ui/src/tokens/base.css, so adding a token there may mean adding
 * it here. cn.test.ts fails if one goes missing.
 */
const twMerge = extendTailwindMerge({
  override: {
    classGroups: {
      // Bare `outline` sets outline-style; tailwind-merge files it under width,
      // where `outline-2` displaces it. Tailwind v4 also defaults
      // --tw-outline-style to solid, so this is about preserving what an author
      // wrote rather than a confirmed rendering fix.
      "outline-style": [{ outline: ["", "solid", "dashed", "dotted", "double", "none", "hidden"] }],
      "outline-w": [{ outline: [isNumber, isArbitraryVariableLength, isArbitraryLength] }],
    },
  },
  extend: {
    classGroups: {
      // background-image utilities, so they stop conflicting with bg-<color>.
      "bg-image": [
        {
          bg: [
            "gradient-card",
            "gradient-card-connected",
            "gradient-card-active",
            "teal-dots",
            "navy-vignette",
          ],
        },
      ],
      // Sizes whose suffix is not a t-shirt size, so they would otherwise be
      // read as text colours and lose to a real one.
      "font-size": [{ text: ["2xs", "display", "lede", "page-title", "section-title"] }],
      rounded: [{ rounded: ["modal"] }],
      shadow: [
        {
          shadow: [
            "button",
            "card",
            "card-active",
            "card-connected",
            "card-hover",
            "focus",
            "microstep",
            "raised",
            "sheet",
            "sleeping",
          ],
        },
      ],
      "font-family": [{ font: ["mono", "sans", "title"] }],
    },
  },
});

/**
 * Merge class names; the last conflicting utility wins. Pass the base recipe
 * first and the caller's overrides last: `cn(OPS_PANEL, className)`.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
